import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Chip,
  Button,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import SidebarMenu from "./AdminPanel/SidebarMenu";
import TopBar from "./AdminPanel/TopBar";
import { useNavigate, useParams, useLocation } from "react-router-dom";

const estadoColor = (estado) => {
  const s = String(estado || "").toLowerCase();
  if (s.includes("esperando")) return "warning";
  if (s.includes("revision")) return "warning";
  if (s.includes("pagado")) return "success";
  if (s.includes("rechazado")) return "error";
  if (s.includes("cancelado")) return "error";
  if (s.includes("pendiente")) return "default";
  return "default";
};

const estadoLabel = (estado) => {
  const s = String(estado || "").toLowerCase();
  if (s === "pendiente") return "Pendiente";
  if (s === "esperando_comprobante") return "Esperando comprobante";
  if (s === "en_revision") return "En revisión";
  if (s === "pagado") return "Pagado";
  if (s === "rechazado") return "Rechazado";
  if (s === "cancelado") return "Cancelado";
  return estado || "Pendiente";
};

const metodoPagoLabel = (metodo) => {
  const s = String(metodo || "").toUpperCase();
  if (s === "TRANSFERENCIA") return "Transferencia bancaria";
  if (s === "TARJETA_CREDITO") return "Tarjeta de crédito";
  return s.replaceAll("_", " ") || "-";
};

const pasarelaLabel = (pasarela) => {
  const s = String(pasarela || "").toUpperCase();
  if (!s || s === "NINGUNA") return "Ninguna";
  if (s === "PAYPAL") return "PayPal";
  if (s === "PAYPHONE") return "PayPhone";
  return s.replaceAll("_", " ");
};

const fmtMoney = (n) => {
  const num = Number(n);
  return Number.isFinite(num) ? num.toFixed(2) : "0.00";
};

const getCookie = (name) => {
  let cookieValue = null;
  if (document.cookie) {
    const cookies = document.cookie.split(";");
    for (let cookie of cookies) {
      const [key, val] = cookie.trim().split("=");
      if (key === name) {
        cookieValue = decodeURIComponent(val);
        break;
      }
    }
  }
  return cookieValue;
};

const Ecommerce = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { ticket } = useParams();

  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [order, setOrder] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAccion, setConfirmAccion] = useState("");

  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");

  const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });

  useEffect(() => {
    const isSuper = localStorage.getItem("is_superuser") === "true";
    const isAdmin = localStorage.getItem("is_admin") === "true";
    const isVendedor = localStorage.getItem("is_vendedor") === "true";
    if (!isSuper && !isAdmin && !isVendedor) navigate("/login");
  }, [location, navigate]);

  const handleSelect = (component) => {
    if (component === "AdminSection") navigate("/admin-panel/secciones");
    else if (component === "ModalInventario") setSubmenuOpen((s) => !s);
    else if (component === "AdminProduct") navigate("/admin-panel/inventario/productos");
    else if (component === "AdminVariant") navigate("/admin-panel/inventario/variantes");
    else if (component === "AdminProforma") navigate("/admin-panel/proformas");
    else if (component === "AdminClient") navigate("/admin-panel/clientes");
    else if (component === "AdminServices") navigate("/admin-panel/servicios");
    else if (component === "AdminPromotion") navigate("/admin-panel/promociones");
    else if (component === "AdminEcommerce") navigate("/admin-panel/ecommerce");
    else navigate("/admin-panel");
  };

  const handleLogout = async () => {
    try {
      await fetch("/sistema/logout/", { method: "POST", credentials: "include" });
    } catch {}
    localStorage.clear();
    navigate("/login");
  };

  const fetchDetalle = async () => {
    setLoading(true);
    setError("");
    try {
      const r = await fetch(`/ecommerce/api/admin/orders/${ticket}/`, { credentials: "include" });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j.error || "No se pudo cargar el pedido");
      setOrder(j.order || null);
      setSelectedStatus(j.order?.estado || "");
    } catch (e) {
      setError(e?.message || "Error al cargar");
      setOrder(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!ticket) return;
    fetchDetalle();
  }, [ticket]);

  const handleDownloadProof = async () => {
    const proofUrl =
      order?.latest_proof_url ||
      order?.proof_url ||
      order?.transfer_proofs?.[0]?.archivo ||
      "";

    if (!proofUrl) {
      setSnack({ open: true, msg: "Este pedido aún no tiene comprobante cargado.", sev: "error" });
      return;
    }

    try {
      const resp = await fetch(proofUrl, { credentials: "include" });
      if (!resp.ok) throw new Error();

      const blob = await resp.blob();
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `${order?.ticket || "comprobante"}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      setSnack({ open: true, msg: "No se pudo descargar el comprobante.", sev: "error" });
    }
  };

  const openConfirm = (accion) => {
    setConfirmAccion(accion);
    setConfirmOpen(true);
  };

  const updateStatus = async (estado) => {
    if (!estado || !order?.ticket) return;

    setSaving(true);
    try {
      const csrftoken = getCookie("csrftoken");
      const r = await fetch(`/ecommerce/api/admin/orders/${order.ticket}/status/`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrftoken
        },
        body: JSON.stringify({ estado })
      });

      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j.error || "No se pudo cambiar el estado");

      setSnack({ open: true, msg: "Estado actualizado", sev: "success" });
      setConfirmOpen(false);
      setStatusDialogOpen(false);
      await fetchDetalle();
    } catch (e) {
      setSnack({ open: true, msg: e?.message || "Error al actualizar", sev: "error" });
    } finally {
      setSaving(false);
    }
  };

  const doAccion = async () => {
    if (!confirmAccion) return;
    await updateStatus(confirmAccion);
  };

  const canDownloadProof =
    Boolean(order?.latest_proof_url) ||
    Boolean(order?.proof_url) ||
    Boolean(order?.transfer_proofs?.[0]?.archivo);

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f6fa" }}>
      <SidebarMenu
        open={open}
        setOpen={setOpen}
        submenuOpen={submenuOpen}
        setSubmenuOpen={setSubmenuOpen}
        handleSelect={handleSelect}
        handleLogout={handleLogout}
        navigate={navigate}
      />

      <Box sx={{ flexGrow: 1 }}>
        <TopBar open={open} setOpen={setOpen} />

        <Box sx={{ width: "95%", mx: "auto", mt: 3, mb: 5 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              gap: 2,
              flexWrap: "wrap"
            }}
          >
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              sx={{ textTransform: "none", bgcolor: "#1565C0", "&:hover": { bgcolor: "#0D47A1" } }}
              onClick={() => navigate("/admin-panel/ecommerce")}
            >
              Regresar
            </Button>

            {order ? (
              <Chip
                label={`Estado: ${estadoLabel(order.estado)}`}
                color={estadoColor(order.estado)}
                sx={{ fontWeight: 800 }}
              />
            ) : null}
          </Box>

          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: "#1565C0", mb: 2 }}>
              Detalle de Venta Ecommerce
            </Typography>

            {loading ? (
              <Box sx={{ py: 5, textAlign: "center" }}>
                <CircularProgress />
                <Typography sx={{ mt: 2 }}>Cargando…</Typography>
              </Box>
            ) : error ? (
              <Alert severity="error">{error}</Alert>
            ) : !order ? (
              <Alert severity="warning">No hay datos.</Alert>
            ) : (
              <>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={4}>
                    <Typography sx={{ fontWeight: 800 }}>Ticket</Typography>
                    <Typography>{order.ticket || "-"}</Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography sx={{ fontWeight: 800 }}>Fecha</Typography>
                    <Typography>{order.creado_en_formatted || order.creado_en || "-"}</Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography sx={{ fontWeight: 800 }}>Método de pago</Typography>
                    <Typography>{metodoPagoLabel(order.metodo_pago)}</Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography sx={{ fontWeight: 800 }}>Pasarela</Typography>
                    <Typography>{pasarelaLabel(order.pasarela)}</Typography>
                    {order.referencia_pasarela ? (
                      <Typography sx={{ color: "#666", fontSize: 13 }}>
                        Ref: {order.referencia_pasarela}
                      </Typography>
                    ) : null}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography sx={{ fontWeight: 800 }}>Método de entrega</Typography>
                    <Typography>
                      {String(order.metodo_entrega || "").replaceAll("_", " ").toUpperCase() || "-"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography sx={{ fontWeight: 800 }}>Cliente</Typography>
                    <Typography>{order.razon_social || "-"}</Typography>
                    <Typography sx={{ color: "#666", fontSize: 13 }}>
                      {order.identificacion || ""}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography sx={{ fontWeight: 800 }}>Contacto</Typography>
                    <Typography>{order.telefono || "-"}</Typography>
                    <Typography sx={{ color: "#666", fontSize: 13 }}>{order.correo || ""}</Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography sx={{ fontWeight: 800 }}>Provincia</Typography>
                    <Typography>{order.provincia || "-"}</Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography sx={{ fontWeight: 800 }}>Ciudad</Typography>
                    <Typography>{order.ciudad || "-"}</Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography sx={{ fontWeight: 800 }}>Dirección</Typography>
                    <Typography>{order.direccion || "-"}</Typography>
                  </Grid>
                </Grid>

                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadProof}
                    disabled={!canDownloadProof}
                    sx={{
                      textTransform: "none",
                      borderColor: "#1565C0",
                      color: "#1565C0",
                      "&:hover": { borderColor: "#0D47A1", color: "#0D47A1" }
                    }}
                  >
                    Descargar comprobante
                  </Button>

                  <Button
                    variant="contained"
                    sx={{ bgcolor: "#1565C0" }}
                    onClick={() => setStatusDialogOpen(true)}
                    disabled={saving}
                    startIcon={<EditIcon />}
                  >
                    Cambiar estado
                  </Button>

                  {String(order.estado || "").toLowerCase() !== "pagado" ? (
                    <Button
                      variant="contained"
                      sx={{ bgcolor: "#2e7d32" }}
                      onClick={() => openConfirm("PAGADO")}
                      disabled={saving}
                      startIcon={<CheckCircleIcon />}
                    >
                      Aceptar
                    </Button>
                  ) : null}

                  {String(order.estado || "").toLowerCase() !== "cancelado" ? (
                    <Button
                      variant="contained"
                      sx={{ bgcolor: "#d32f2f" }}
                      onClick={() => openConfirm("CANCELADO")}
                      disabled={saving}
                      startIcon={<CancelIcon />}
                    >
                      Cancelar
                    </Button>
                  ) : null}
                </Box>

                <Paper sx={{ p: 0, borderRadius: 2, overflow: "hidden", border: "1px solid #e6e6e6" }}>
                  <Box sx={{ bgcolor: "#1565C0", px: 2, py: 1.25 }}>
                    <Typography sx={{ color: "#fff", fontWeight: 700 }}>Productos</Typography>
                  </Box>

                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 900 }}>Código</TableCell>
                        <TableCell sx={{ fontWeight: 900 }}>Descripción</TableCell>
                        <TableCell sx={{ fontWeight: 900 }} align="right">Cant</TableCell>
                        <TableCell sx={{ fontWeight: 900 }} align="right">P.Unit</TableCell>
                        <TableCell sx={{ fontWeight: 900 }} align="right">Descuento</TableCell>
                        <TableCell sx={{ fontWeight: 900 }} align="right">IVA</TableCell>
                        <TableCell sx={{ fontWeight: 900 }} align="right">Subtotal</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {!order.items || order.items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7}>
                            <Typography sx={{ py: 2, color: "#666" }}>Sin productos</Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        order.items.map((it) => (
                          <TableRow key={it.id}>
                            <TableCell>
                              <Typography sx={{ fontWeight: 700 }}>{it.code || it.reference || "-"}</Typography>
                              {String(it.sku || "").trim() ? (
                                <Typography sx={{ color: "#666", fontSize: 12 }}>SKU: {it.sku}</Typography>
                              ) : null}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: "pre-line" }}>
                              <Typography sx={{ fontWeight: 700 }}>{it.name || ""}</Typography>
                              {String(it.brand || "").trim() ? (
                                <Typography sx={{ color: "#666", fontSize: 12 }}>Marca: {it.brand}</Typography>
                              ) : null}
                            </TableCell>
                            <TableCell align="right">{Number(it.quantity || 0)}</TableCell>
                            <TableCell align="right">${Number(it.unit_price_with_iva || 0).toFixed(2)}</TableCell>
                            <TableCell align="right">${Number(it.line_discount_total || 0).toFixed(2)}</TableCell>
                            <TableCell align="right">${Number(it.line_total_iva || 0).toFixed(2)}</TableCell>
                            <TableCell align="right">${Number(it.line_total_with_iva || 0).toFixed(2)}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </Paper>

                <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                  <Paper sx={{ p: 2, width: 380, maxWidth: "100%", border: "1px solid #e6e6e6" }}>
                    {[
                      ["Subtotal", order.subtotal],
                      ["Descuento", order.descuento_total],
                      ["IVA", order.iva_total],
                      ["Envío", order.costo_envio],
                      ["Recargo tarjeta", order.card_surcharge_amount],
                      ["Total", order.total]
                    ].map(([label, value]) => (
                      <Box key={label} sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
                        <Typography sx={{ fontWeight: label === "Total" ? 900 : 600 }}>{label}</Typography>
                        <Typography sx={{ fontWeight: label === "Total" ? 900 : 700 }}>
                          ${Number(value || 0).toFixed(2)}
                        </Typography>
                      </Box>
                    ))}
                  </Paper>
                </Box>
              </>
            )}
          </Paper>
        </Box>
      </Box>

      <Dialog
        open={confirmOpen}
        onClose={saving ? undefined : () => setConfirmOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 900 }}>Confirmar</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontWeight: 800, mb: 1.5 }}>
            ¿Seguro que desea cambiar el estado a {estadoLabel(confirmAccion)}?
          </Typography>

          {saving ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 }}>
              <CircularProgress size={22} />
              <Typography>Procesando…</Typography>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={saving} sx={{ fontWeight: 800 }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={doAccion}
            disabled={saving}
            sx={{ bgcolor: "#1565C0", fontWeight: 900 }}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={statusDialogOpen}
        onClose={saving ? undefined : () => setStatusDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 900 }}>Cambiar estado</DialogTitle>
        <DialogContent dividers>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id="estado-select-label">Estado</InputLabel>
            <Select
              labelId="estado-select-label"
              value={selectedStatus}
              label="Estado"
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              {(order?.allowed_statuses || []).map((estado) => (
                <MenuItem key={estado} value={estado}>
                  {estadoLabel(estado)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {saving ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 }}>
              <CircularProgress size={22} />
              <Typography>Guardando…</Typography>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setStatusDialogOpen(false)} disabled={saving} sx={{ fontWeight: 800 }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => updateStatus(selectedStatus)}
            disabled={saving || !selectedStatus || selectedStatus === order?.estado}
            sx={{ bgcolor: "#1565C0", fontWeight: 900 }}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={snack.open} onClose={() => setSnack((s) => ({ ...s, open: false }))} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>{snack.sev === "error" ? "Error" : "Listo"}</DialogTitle>
        <DialogContent dividers>
          <Alert severity={snack.sev} sx={{ mb: 0 }}>
            {snack.msg}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setSnack((s) => ({ ...s, open: false }))} sx={{ fontWeight: 900 }}>
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Ecommerce;